import React, { useState, useEffect, useCallback } from 'react';
import { UserRole, Tool, Person, Warehouse, AssignmentLogEntry, ViewMode, Users, ToolHistory, Classes } from '../../types';
import { RESTCLASSES, RESTHISTORY, RESTPOSTHISTORY, RESTTOOL, RESTUSER, RESTWAREHOUSES } from '../../constants';

import ToolGrid from './ToolGrid';
import SearchBar from './SearchBar';
import AssignmentModal from './AssignmentModal';
import HistoryView from './HistoryView';



import { WarehouseIcon, UserIcon, HistoryIcon,Hammer, ToolIcon, PlusCircleIcon, SledgeHammer } from './Icons';
import { Refresh } from '@mui/icons-material';
import WarehouseView from './WarehouseView';

interface ToolspageProps {
  role: UserRole;
  token:string;
}

const ToolsPage: React.FC<ToolspageProps> = ({token,role}) => {
  const [tools, setTools] = useState<Tool[]>([]);
  const [people, setUser] = useState<Users[]>([]);
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [history, setHistory] = useState<ToolHistory[]>([]);
  const [classes, setClasses] = useState<Classes[]>([]);
  const [assignmentLogs, setAssignmentLogs] = useState<AssignmentLogEntry[]>([]);
  
  const [selectedWarehouseId, setSelectedWarehouseId] = useState<number | null>(null);
  const [selectedToolIds, setSelectedToolIds] = useState<number[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [isAssignmentModalOpen, setIsAssignmentModalOpen] = useState<boolean>(false);
  const [currentView, setCurrentView] = useState<ViewMode>(ViewMode.Tools);

  const [filterWarehouseId, setFilterWarehouseId] = useState<number | null>(null);
  
  const [refresh,setRefresh]=useState<number>(0);

  const handleToggleToolSelect = (toolId: number) => {
    setSelectedToolIds(prevSelected =>
      prevSelected.includes(toolId)
        ? prevSelected.filter(id => id !== toolId)
        : [...prevSelected, toolId]
    );
  };

  useEffect(()=>{
    const fetchtool = async()=>{
      const response = await RESTTOOL(token);
      setTools(response);
    };
    const fetchuser = async()=>{
      const response = await RESTUSER(token);
      setUser(response);
    };
    const fetchwarehouse = async()=>{
      const response = await RESTWAREHOUSES(token);
      setWarehouses(response);
    };
    const fetchhistory = async()=>{
      const response = await RESTHISTORY(token);
      setHistory(response);
    };

    const fetchclasses = async()=>{
      const response = await RESTCLASSES(token);
      setClasses(response);
    };

    fetchtool();
    fetchuser();
    fetchwarehouse();
    fetchhistory();
    fetchclasses();
  },[token, refresh]);

  const handleAssignTools = async (assigneeId: number, destination: string, photoFile?: File | null, notes?: string) => {
    const newLogEntryId = `log_${Date.now()}`;
    let photoDataUrl: string | null = null;

    if (photoFile) {
      photoDataUrl = await new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result as string);
        reader.readAsDataURL(photoFile);
      });
    }

   

    const newLogEntry: AssignmentLogEntry = {
      //id: newLogEntryId,
      id: -1,
      toolIds: [...selectedToolIds],
      assigneeId: -1,
      destination,
      assignmentDate: new Date().toISOString(),
      confirmationPhoto: photoDataUrl,
      notes: notes,
    };

    const hists:ToolHistory[] = selectedToolIds.map( tid =>{
      return {
        userid: assigneeId,
        toolid: tid,
        assignimgsrc: photoDataUrl?.split(',')[1],
        assigned: 0,
        destination: destination,
        assignnote: notes,
      }
    });
    
    hists.forEach(h => {
      RESTPOSTHISTORY(h,token).then(() => { 
        RESTHISTORY(token).then(response => setHistory(response));
      })
    });

    
    /*setAssignmentLogs(prevLogs => [...prevLogs, newLogEntry]);
    
    setTools(prevTools =>
      prevTools.map(tool =>
        selectedToolIds.includes(tool.id!)
          ? { ...tool, warehouseId: null, currentAssignmentLogId: newLogEntryId }
          : tool
      )
    );*/
    setSelectedToolIds([]);
    setIsAssignmentModalOpen(false);
   
  };

  const handleReturnTools = (logEntryId: number) => {
    const logEntry = assignmentLogs.find(log => log.id === logEntryId);
    if (!logEntry) return;
    
    // For simplicity, tools are returned to the first warehouse or a default.
    // A more complex system might allow choosing the return warehouse or remember original.
    const returnWarehouseId = warehouses[0]?.id || null; 

    setAssignmentLogs(prevLogs => 
      prevLogs.map(log => 
        log.id === logEntryId ? { ...log, returnDate: new Date().toISOString() } : log
      )
    );

    setTools(prevTools => 
      prevTools.map(tool => {
        if (logEntry.toolIds.includes(tool.id!)){
          // && tool.currentAssignmentLogId === logEntryId) {
          return { ...tool, warehouseId: returnWarehouseId, currentAssignmentLogId: null };
        }
        return tool;
      })
    );
  };

  const toolsForAssignmentModal = tools.filter(tool => selectedToolIds.includes(tool.id!));

  return (
    <div className="min-h-screen bg-slate-900 text-slate-100 flex flex-col">
      <header className="bg-slate-800 shadow-md p-4 sticky top-0 z-40">
        <div className="container mx-auto flex flex-col sm:flex-row justify-between items-center">
          
          <nav className="flex space-x-2 sm:space-x-4">
            <div className="grid grid-cols-6 gap-4">
            {(Object.values(ViewMode) as ViewMode[]).map(view => (
              <button
                type="button"
                key={view}
                onClick={() => setCurrentView(view)}
                className={`col-span-2 px-4 py-2 rounded-md font-medium transition-colors
                  ${currentView === view ? 'bg-sky-600 text-white' : 'bg-slate-700 hover:bg-slate-600 text-slate-300'}
                `}
              >
                {view === ViewMode.Tools && (<SledgeHammer className="w-12 h-12 inline mr-1.5" />)}
                {view === ViewMode.Tools && 'Tutti gli attrezzi' }
                {view === ViewMode.Warehouses && (<WarehouseIcon className="w-12 h-12 inline mr-1.5" />)}
                {view === ViewMode.Warehouses && 'Magazzini' }
                {view === ViewMode.Personel && (<UserIcon className="w-12 h-12 inline mr-1.5" />)}
                {view === ViewMode.Personel && 'Personale' }
                
                
              </button>
            ))}
            {currentView === ViewMode.Tools && (
            <div className="col-span-2">
              <label htmlFor="search-bar" className="block text-sm font-medium text-slate-300 mb-1">
                  Cerca:
              </label>
                  <SearchBar searchTerm={searchTerm} onSearchTermChange={setSearchTerm} />
              </div>)}
            
            </div>
          </nav>
        </div>
      </header>

      <main className="flex-grow container mx-auto p-0 sm:p-4">
        
        {currentView === ViewMode.Tools && (
          <>
            <ToolGrid
              tools={tools}
              warehouses={warehouses}
              people={people}
              history={history}
              classes={classes}
              assignmentLogs={assignmentLogs}
              selectedToolIds={selectedToolIds}
              onToggleToolSelect={handleToggleToolSelect}
              searchTerm={searchTerm}
              selectedWarehouseId={selectedWarehouseId}
            />

            {selectedToolIds.length > 0 && (
              <div className="fixed bottom-6 right-6 z-40">
                <button
                  type="button"
                  onClick={() => setIsAssignmentModalOpen(true)}
                  className="bg-sky-600 hover:bg-sky-500 text-white font-semibold py-3 px-6 rounded-full shadow-lg transition-transform hover:scale-105 flex items-center"
                >
                  <PlusCircleIcon className="w-6 h-6 mr-2" />
                  Assegna {selectedToolIds.length} Attrezzi
                </button>
              </div>
            )}
          </>
        )}

        {currentView === ViewMode.Warehouses && (
          <>
            <div className="mb-6 flex flex-col sm:flex-row gap-4">
              <select
                value={filterWarehouseId || ''}
                onChange={(e) => setFilterWarehouseId(Number(e.target.value) || null)}
                className="p-3 rounded-lg bg-slate-800 text-slate-200 border border-slate-700 focus:ring-2 focus:ring-sky-500 focus:border-sky-500 outline-none min-w-[200px]"
                aria-label="Filtra per magazzino"
              >
                <option value="">Tutti i magazzini</option>
                {warehouses.map(p => <option key={p.id} value={p.id}>{p.name} {p.location}</option>)}
              </select>
              <input
                type="text"
                placeholder="Cerca (persona, attrezzo, destinazione)..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="flex-grow p-3 rounded-lg bg-slate-800 text-slate-200 border border-slate-700 focus:ring-2 focus:ring-sky-500 focus:border-sky-500 outline-none placeholder-slate-500"
              />
            </div>
            <ToolGrid
              tools={tools}
              warehouses={warehouses}
              people={people}
              history={history}
              classes={classes}
              assignmentLogs={assignmentLogs}
              selectedToolIds={selectedToolIds}
              onToggleToolSelect={handleToggleToolSelect}
              searchTerm={searchTerm}
              selectedWarehouseId={filterWarehouseId}
            />

            {selectedToolIds.length > 0 && (
              <div className="fixed bottom-6 right-6 z-40">
                <button
                  type="button"
                  onClick={() => setIsAssignmentModalOpen(true)}
                  className="bg-sky-600 hover:bg-sky-500 text-white font-semibold py-3 px-6 rounded-full shadow-lg transition-transform hover:scale-105 flex items-center"
                >
                  <PlusCircleIcon className="w-6 h-6 mr-2" />
                  Assegna {selectedToolIds.length} Attrezzi
                </button>
              </div>
            )}
          </>
        )}

        {currentView === ViewMode.Personel && (
          <HistoryView 
            assignmentLogs={history} 
            tools={tools} 
            people={people}
            history={history}
            warehouses={warehouses}
            onReturnTools={handleReturnTools}
          />
        )}

        
      </main>

      
      <AssignmentModal
        isOpen={isAssignmentModalOpen}
        onClose={() => {setIsAssignmentModalOpen(false); setRefresh(refresh+1);}}
        selectedTools={toolsForAssignmentModal}
        people={people}
        onAssign={handleAssignTools}
      />
    </div>
  );
};

/*
<label htmlFor="warehouse-select" className="block text-sm font-medium text-slate-300 mb-1">
                    <WarehouseIcon className="w-5 h-5 inline mr-1.5 align-text-bottom" />Seleziona Magazzino:
              </label>
              <select
                    disabled={role === UserRole.User}
                    id="warehouse-select"
                    value={selectedWarehouseId || ''}
                    onChange={(e) => {setSelectedWarehouseId(Number.parseInt(e.target.value) || null); setSelectedToolIds([]);}}
                    className="col-start-1 col-span-3 p-3 rounded-lg bg-slate-700 text-slate-200 border border-slate-600 focus:ring-2 focus:ring-sky-500 focus:border-sky-500 outline-none"
                  >
                    <option value="">Tutti i Magazzini (Mostra anche assegnati)</option>
                    {warehouses.map(wh => (
                      <option key={wh.id} value={wh.id}>{wh.name}</option>
                    ))}
              </select>
              */
/*

<div className="bg-slate-800 p-4 rounded-lg shadow-lg mb-6 sticky top-[84px] z-30"> 

<div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
                <div className="md:col-span-1">
                  <label htmlFor="warehouse-select" className="block text-sm font-medium text-slate-300 mb-1">
                    <WarehouseIcon className="w-5 h-5 inline mr-1.5 align-text-bottom" />Seleziona Magazzino:
                  </label>
                  <select
                    id="warehouse-select"
                    value={selectedWarehouseId || ''}
                    onChange={(e) => {setSelectedWarehouseId(e.target.value || null); setSelectedToolIds([]);}}
                    className="w-full p-3 rounded-lg bg-slate-700 text-slate-200 border border-slate-600 focus:ring-2 focus:ring-sky-500 focus:border-sky-500 outline-none"
                  >
                    <option value="">Tutti i Magazzini (Mostra anche assegnati)</option>
                    {warehouses.map(wh => (
                      <option key={wh.id} value={wh.id}>{wh.name}</option>
                    ))}
                  </select>
                </div>
                <div className="md:col-span-2">
                   <label htmlFor="search-bar" className="block text-sm font-medium text-slate-300 mb-1">
                    Cerca Attrezzi:
                  </label>
                  <SearchBar searchTerm={searchTerm} onSearchTermChange={setSearchTerm} />
                </div>
              </div>
            </div>

<footer className="bg-slate-800 text-center p-4 text-sm text-slate-500 border-t border-slate-700">
        © {new Date().getFullYear()} Tool Tracker Pro. Tutti i diritti riservati.
      </footer>
*/
export default ToolsPage;